/**
 * Financial Data API Client
 *
 * Provides methods for interacting with financial data endpoints.
 */

import axios from 'axios';

const API_URL = process.env.EXPO_PUBLIC_API_URL || 'https://topnote-api-sbp5f6nlfa-uc.a.run.app';

export interface Instrument {
  id: number;
  symbol: string;
  company_name?: string;
  name_sch?: string; // Simplified Chinese name
  name_tch?: string; // Traditional Chinese name
  sector?: string;
  industry?: string;
  asset_type?: string;
  exchange?: string;
  currency: string;
  description?: string;
}

export interface InstrumentCandidate {
  id: number;
  symbol: string;
  company_name?: string;
  exchange?: string;
  currency: string;
}

export interface ResolutionResult {
  status: 'matched' | 'unmatched' | 'ambiguous';
  instrument_id?: number;
  normalized_symbol: string;
  candidates: InstrumentCandidate[];
  linked_trades?: number;
  linked_positions?: number;
}

export interface CompanyProfile {
  symbol: string;
  company_name?: string;
  description?: string;
  ceo?: string;
  headquarters_location?: string;
  website?: string;
  employees?: number;
  founded_year?: number;
  industry_group?: string;
  business_sector?: string;
  image_url?: string;
  data_freshness: {
    status: string;
    last_update?: string;
    next_update?: string;
  };
}

export interface Quote {
  symbol: string;
  price?: number;
  changes_percentage?: number;
  change?: number;
  day_high?: number;
  day_low?: number;
  volume?: number;
  timestamp?: string;
  data_freshness: {
    status: string;
    last_update?: string;
    next_update?: string;
  };
}

export interface HistoricalPrice {
  date: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  adj_close?: number;
  volume?: number;
}

export interface HistoricalPricesResponse {
  symbol: string;
  prices: HistoricalPrice[];
}

class FinancialDataAPI {
  private baseURL: string;

  constructor() {
    this.baseURL = API_URL;
  }

  private getHeaders(token: string) {
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Resolve symbol to instrument
   */
  async resolveInstrument(
    symbol: string,
    currency: string,
    token: string,
    userId?: number
  ): Promise<ResolutionResult> {
    const requestBody: any = { symbol, currency };
    if (userId) {
      requestBody.user_id = userId;
    }

    const response = await axios.post(
      `${this.baseURL}/api/v1/financial-data/instruments/resolve`,
      requestBody,
      { headers: this.getHeaders(token) }
    );
    return response.data;
  }

  /**
   * Get instrument details by ID
   */
  async getInstrument(instrumentId: number, token: string): Promise<Instrument> {
    const response = await axios.get(
      `${this.baseURL}/api/v1/financial-data/instruments/${instrumentId}`,
      { headers: this.getHeaders(token) }
    );
    return response.data;
  }

  /**
   * Search instruments
   */
  async searchInstruments(query: string, limit = 10, token: string): Promise<Instrument[]> {
    const response = await axios.get(`${this.baseURL}/api/v1/financial-data/instruments`, {
      params: { query, limit },
      headers: this.getHeaders(token),
    });
    return response.data;
  }

  /**
   * Get company profile for an instrument (User Story 2)
   */
  async getInstrumentProfile(symbol: string, token: string): Promise<CompanyProfile> {
    const response = await axios.get(
      `${this.baseURL}/api/v1/financial-data/instruments/${symbol}/profile`,
      { headers: this.getHeaders(token) }
    );
    return response.data;
  }

  /**
   * Get latest quote for an instrument (User Story 2)
   */
  async getQuote(instrument_id: number, token: string): Promise<Quote> {
    const response = await axios.get(
      `${this.baseURL}/api/v1/financial-data/instruments/by-id/${instrument_id}/quote`,
      { headers: this.getHeaders(token) }
    );
    return response.data;
  }

  /**
   * Get historical prices for an instrument (User Story 2)
   */
  async getHistoricalPrices(
    instrument_id: number,
    token: string,
    limit?: number,
    from_date?: string,
    to_date?: string
  ): Promise<HistoricalPricesResponse> {
    const params: any = {};
    if (limit) params.limit = limit;
    if (from_date) params.from_date = from_date;
    if (to_date) params.to_date = to_date;

    const response = await axios.get(
      `${this.baseURL}/api/v1/financial-data/instruments/by-id/${instrument_id}/prices`,
      {
        params,
        headers: this.getHeaders(token),
      }
    );
    return response.data;
  }
}

export const financialDataAPI = new FinancialDataAPI();
